import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PeopleIcon from '@mui/icons-material/People';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import notificationApi from '../../services/notificationApi';
import { usePushNotifications } from '../../contexts/PushNotificationContext';

const TYPE_ICON = {
  connection_request:  <PeopleIcon fontSize="small" color="primary" />,
  connection_accepted: <PeopleIcon fontSize="small" color="success" />,
  grouping_complete:   <AutorenewIcon fontSize="small" color="success" />,
  photo_request:       <PhotoLibraryIcon fontSize="small" color="primary" />,
  photo_shared:        <PhotoLibraryIcon fontSize="small" color="secondary" />,
  test:                <InfoIcon fontSize="small" color="action" />,
  general:             <NotificationsNoneIcon fontSize="small" color="action" />,
};

const TYPE_LABEL = {
  connection_request:  'Connection',
  connection_accepted: 'Connection',
  grouping_complete:   'Grouping',
  photo_request:       'Photos',
  photo_shared:        'Photos',
  test:                'Test',
  general:             'General',
};

function timeAgo(isoString) {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)   return `${days}d ago`;
  return new Date(isoString).toLocaleDateString();
}

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { clearUnread, refreshUnreadCount } = usePushNotifications() || {};
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [loadingMore, setLoadingMore]     = useState(false);
  const [page, setPage]                   = useState(1);
  const [hasNext, setHasNext]             = useState(false);
  const [filter, setFilter]               = useState('all');
  const [error, setError]                 = useState('');
  const markedRef = useRef(new Set());

  const load = useCallback(async (pg = 1, flt = filter, replace = true) => {
    replace ? setLoading(true) : setLoadingMore(true);
    setError('');
    try {
      const data = await notificationApi.getNotifications(pg, flt);
      setNotifications((prev) => replace ? data.notifications : [...prev, ...data.notifications]);
      setHasNext(data.has_next);
      setPage(pg);
    } catch {
      setError('Failed to load notifications.');
    } finally {
      replace ? setLoading(false) : setLoadingMore(false);
    }
  }, [filter]);

  useEffect(() => { load(1, filter, true); }, [filter]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-mark visible unread notifications as read
  useEffect(() => {
    if (loading || !notifications.length) return;

    const unread = notifications.filter((n) => !n.read_status && !markedRef.current.has(n.id));
    if (!unread.length) return;

    unread.forEach((n) => markedRef.current.add(n.id));

    Promise.allSettled(unread.map((n) => notificationApi.markAsRead(n.id))).then(() => {
      setNotifications((prev) =>
        prev.map((n) =>
          markedRef.current.has(n.id) ? { ...n, read_status: new Date().toISOString() } : n
        )
      );
      clearUnread?.();
      refreshUnreadCount?.();
    });
  }, [loading, notifications, clearUnread, refreshUnreadCount]);

  const handleDelete = async (id) => {
    try {
      await notificationApi.deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch {
      // ignore
    }
  };

  const handleMarkAllRead = async () => {
    await notificationApi.markAllRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, read_status: new Date().toISOString() })));
    clearUnread?.();
  };

  const handleClick = (notification) => {
    if (notification.action_url && notification.action_url !== '/notifications') {
      navigate(notification.action_url);
    }
  };

  const hasUnread = notifications.some((n) => !n.read_status);

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
        {/* ── Header ──────────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsActiveIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>Notifications</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Select
              size="small"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              sx={{ minWidth: 120, fontSize: 14 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="connection_request">Connections</MenuItem>
              <MenuItem value="photo_shared">Photos</MenuItem>
              <MenuItem value="grouping_complete">Grouping</MenuItem>
            </Select>
            {hasUnread && (
              <Tooltip title="Mark all as read">
                <IconButton size="small" onClick={handleMarkAllRead}>
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── List ────────────────────────────────────────────────────── */}
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {loading ? (
            <List disablePadding>
              {[...Array(5)].map((_, i) => (
                <React.Fragment key={i}>
                  <ListItem sx={{ py: 1.5 }}>
                    <ListItemAvatar><Skeleton variant="circular" width={40} height={40} /></ListItemAvatar>
                    <ListItemText
                      primary={<Skeleton width="60%" />}
                      secondary={<Skeleton width="40%" />}
                    />
                  </ListItem>
                  {i < 4 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : notifications.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No notifications yet</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map((n, idx) => {
                const isUnread = !n.read_status;
                const icon = TYPE_ICON[n.noti_type] || TYPE_ICON.general;
                const clickable = n.action_url && n.action_url !== '/notifications';
                return (
                  <React.Fragment key={n.id}>
                    <ListItem
                      alignItems="flex-start"
                      onClick={() => clickable && handleClick(n)}
                      sx={{
                        py: 1.5,
                        cursor: clickable ? 'pointer' : 'default',
                        bgcolor: isUnread ? 'action.hover' : 'transparent',
                        '&:hover': clickable ? { bgcolor: 'action.selected' } : {},
                        transition: 'background-color 0.2s',
                      }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleDelete(n.id); }}
                          sx={{ color: 'text.disabled' }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'grey.100', width: 40, height: 40 }}>
                          {icon}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <Typography
                              variant="body2"
                              fontWeight={isUnread ? 600 : 400}
                              sx={{ flex: 1, minWidth: 0 }}
                            >
                              {n.message}
                            </Typography>
                            {isUnread && (
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                            <Chip
                              label={TYPE_LABEL[n.noti_type] || 'General'}
                              size="small"
                              sx={{ height: 18, fontSize: 10 }}
                            />
                            <Typography variant="caption" color="text.disabled">
                              {timeAgo(n.date_received)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {idx < notifications.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Paper>

        {/* ── Load more ───────────────────────────────────────────────── */}
        {hasNext && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => load(page + 1, filter, false)}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={14} /> : null}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          </Box>
        )}
    </Container>
  );
};

export default NotificationsPage;
